<template>
  <TransitionRoot as="template" :show="show">
    <Dialog as="div" class="relative zoom-80 z-50" @close="$emit('close')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="
                relative
                transform
                overflow-hidden
                rounded-lg
                bg-white
                px-4
                pt-5
                pb-4
                text-left
                shadow-xl
                transition-all
                sm:my-8
                sm:w-full
                sm:max-w-lg
                sm:p-4
              "
            >
              <div>
                <div class="text-center sm:text-left">
                  <div class="w-full pb-4 inline-flex items-center border-b border-gray-200">
                    <DialogTitle as="h2" class="text-lg leading-6 font-semibold text-gray-900">
                      Share {{ shareableType }}
                    </DialogTitle>

                    <!-- Close Button -->
                    <button
                      type="button"
                      class="
                        ms-auto
                        inline-flex
                        items-center
                        justify-center
                        h-6
                        w-6
                        ml-auto
                        opacity-60
                        hover:opacity-80
                      "
                      aria-label="Close"
                      @click="$emit('close')"
                    >
                      <span class="sr-only">Close</span>
                      <CrossedBlackIcon />
                    </button>
                  </div>
                  <!-- Tabs (Students | Clinicians)-->
                  <div>
                    <nav class="w-full flex" aria-label="Tabs">
                      <!-- Students -->
                      <button
                        @click="setTab('student')"
                        :class="[
                          tab === 'student'
                            ? 'border-b-2 border-vice-teal-400 text-vice-teal-600'
                            : 'border-b border-transparent text-gray-500 hover:text-gray-700 border-gray-300',
                          'w-1/2 py-3 px-1 text-center font-medium text-xs',
                        ]"
                      >
                        Students
                      </button>
                      <!-- Clinicians -->
                      <button
                        @click="setTab('clinician')"
                        :class="[
                          tab === 'clinician'
                            ? 'border-b-2 border-vice-teal-400 text-vice-teal-600'
                            : 'border-b border-transparent text-gray-500 hover:text-gray-700 border-gray-300',
                          'w-1/2 py-3 px-1 text-center font-medium text-xs',
                        ]"
                      >
                        Clinicians
                      </button>
                    </nav>
                  </div>
                  <div class="mt-4">
                    <!-- Search/Filter -->
                    <div class="mb-3 relative">
                      <LensIcon />
                      <input
                        v-model="searchQuery"
                        type="text"
                        :placeholder="`Search ${tab}s...`"
                        class="
                          w-full
                          pl-9
                          pr-3
                          py-2
                          border border-gray-300
                          rounded-md
                          text-sm
                          focus:outline-none
                          focus:ring-2 focus:ring-vice-teal-500
                        "
                      />
                    </div>

                    <!-- Loading State -->
                    <div v-if="loading" class="flex justify-center py-8">
                      <ViceLoader colorClass="text-vice-teal-700" size="h-8 w-8" />
                    </div>

                    <!-- No Users Available -->
                    <div
                      v-else-if="availableByRole.length === 0"
                      class="text-center py-8 text-gray-500"
                    >
                      No users available to share with
                    </div>

                    <!-- User List -->
                    <div v-else class="max-h-100 overflow-y-auto border rounded-md">
                      <!-- Select All -->
                      <div class="flex items-center px-4 py-3 border-b bg-gray-50 sticky top-0">
                        <input
                          id="select-all"
                          type="checkbox"
                          :checked="allSelected"
                          @change="toggleSelectAll"
                          class="
                            h-4
                            w-4
                            text-vice-teal-600
                            focus:ring-vice-teal-500
                            border-gray-300
                            rounded
                          "
                        />
                        <label for="select-all" class="ml-3 text-sm font-medium text-gray-700">
                          Select all {{ tab }}s ({{ filteredUsers.length }})
                        </label>
                      </div>

                      <!-- User Items -->
                      <div
                        v-for="(user, index) in filteredUsers"
                        :key="user.id"
                        class="flex items-center px-4 py-3 hover:bg-gray-50"
                        :class="index === filteredUsers.length - 1 ? 'border-b-0' : 'border-b'"
                      >
                        <input
                          :id="`user-${user.id}`"
                          type="checkbox"
                          :value="user.id"
                          v-model="selectedUserIds"
                          class="
                            h-4
                            w-4
                            text-vice-teal-600
                            focus:ring-vice-teal-500
                            border-gray-300
                            rounded
                          "
                        />
                        <label :for="`user-${user.id}`" class="ml-3 flex-1 cursor-pointer">
                          <div class="flex items-center justify-between space-x-3">
                            <div class="flex space-x-4">
                              <img
                                v-if="user.profile_photo_url"
                                :src="user.profile_photo_url"
                                :alt="user.name"
                                class="h-8 w-8 rounded-full object-cover"
                              />
                              <div
                                v-else
                                class="
                                  h-8
                                  w-8
                                  rounded-full
                                  bg-vice-teal-100
                                  flex
                                  items-center
                                  justify-center
                                  text-vice-teal-700
                                  font-semibold
                                  text-xs
                                "
                              >
                                {{ getUserInitials(user.name) }}
                              </div>
                              <div>
                                <p class="text-sm font-medium text-gray-900">
                                  {{ user.name }}
                                </p>
                                <p class="text-xs text-gray-500">{{ user.email }}</p>
                              </div>
                            </div>
                            <span
                              class="
                                inline-flex
                                items-center
                                px-2.5
                                py-0.5
                                rounded-full
                                text-xxs
                                font-medium
                                text-gray-600
                              "
                              :class="
                                user.role === 'student' ? 'bg-vice-teal-100' : 'bg-vice-light-blue'
                              "
                            >
                              {{ user.role.charAt(0).toUpperCase() + user.role.slice(1) }}
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  @click="shareWithUsers"
                  :disabled="selectedUserIds.length === 0 || sharing"
                  class="
                    inline-flex
                    w-full
                    justify-center
                    rounded-md
                    border border-transparent
                    bg-vice-teal-600
                    px-4
                    py-2
                    text-base
                    font-medium
                    text-white
                    shadow-sm
                    hover:bg-vice-teal-700
                    focus:outline-none
                    focus:ring-2 focus:ring-vice-teal-500 focus:ring-offset-2
                    sm:ml-3
                    sm:w-auto
                    sm:text-sm
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                >
                  <ViceLoader v-if="sharing" class="h-4 w-4 mr-2" />
                  <span v-else>Share ({{ selectedUserIds.length }})</span>
                </button>
                <button
                  type="button"
                  @click="$emit('close')"
                  :disabled="sharing"
                  class="
                    mt-3
                    inline-flex
                    w-full
                    justify-center
                    rounded-md
                    border border-gray-300
                    bg-white
                    px-4
                    py-2
                    text-base
                    font-medium
                    text-gray-700
                    shadow-sm
                    hover:bg-gray-50
                    focus:outline-none
                    focus:ring-2 focus:ring-vice-teal-500 focus:ring-offset-2
                    sm:mt-0
                    sm:w-auto
                    sm:text-sm
                  "
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import CrossedBlackIcon from '@/Components/Icons/CrossedBlackIcon.vue';
import LensIcon from '@/Components/Icons/LensIcon.vue';

export default {
  name: 'AddUsersModal',
  components: {
    LensIcon,
    CrossedBlackIcon,
    Dialog,
    ViceLoader,
    DialogPanel,
    DialogTitle,
    TransitionRoot,
    TransitionChild,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    sessionId: {
      type: Number,
      required: true,
    },
    shareableType: {
      type: String,
      required: true,
    },
    shareableId: {
      type: Number,
      required: true,
    },
    alreadySharedUserIds: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'users-added'],
  setup(props, { emit }) {
    const tab = ref('student');
    const loading = ref(true);
    const sharing = ref(false);
    const searchQuery = ref('');
    const availableUsers = ref([]);
    const selectedUserIds = ref([]);

    const filteredUsers = computed(() => {
      if (!searchQuery.value) {
        return availableByRole.value;
      }

      const query = searchQuery.value.toLowerCase();
      return availableByRole.value.filter(
        (user) =>
          user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query),
      );
    });

    const availableByRole = computed(() =>
      availableUsers.value.filter((user) => user.role === tab.value),
    );

    const allSelected = computed(
      () =>
        filteredUsers.value.length > 0 &&
        filteredUsers.value.every((user) => selectedUserIds.value.includes(user.id)),
    );

    const setTab = (value) => {
      tab.value = value;
    };

    const fetchAvailableUsers = async () => {
      loading.value = true;
      try {
        const response = await fetch(route('shareables.available', props.sessionId));
        const data = await response.json();
        if (data.success) {
          // Filter out already shared users
          availableUsers.value = data.data.filter(
            (user) => !props.alreadySharedUserIds.includes(user.id),
          );
        }
      } catch (error) {
        console.error('Error fetching available users:', error);
      } finally {
        loading.value = false;
      }
    };

    const toggleSelectAll = () => {
      if (allSelected.value) {
        selectedUserIds.value = [];
      } else {
        selectedUserIds.value = filteredUsers.value.map((user) => user.id);
      }
    };

    const shareWithUsers = async () => {
      if (selectedUserIds.value.length === 0) return;

      sharing.value = true;

      try {
        // Map selected user IDs to include their roles
        const recipients = selectedUserIds.value.map((userId) => {
          const user = availableUsers.value.find((u) => u.id === userId);
          return {
            id: userId,
            role: user?.role || 'student',
          };
        });

        const response = await axios.post(route('shareables.store'), {
          shareable_type: props.shareableType,
          shareable_id: props.shareableId,
          recipients,
        });

        if (response.data.success) {
          emit('users-added', response.data.data);
        } else {
          console.log('Failed to share content.');
        }
      } catch (error) {
        console.error('Error sharing content:', error);
      } finally {
        sharing.value = false;
      }
    };

    const getUserInitials = (name) => {
      if (!name) return '??';

      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    };

    onMounted(() => {
      fetchAvailableUsers();
    });

    return {
      tab,
      loading,
      sharing,
      searchQuery,
      allSelected,
      filteredUsers,
      availableByRole,
      selectedUserIds,
      setTab,
      shareWithUsers,
      getUserInitials,
      toggleSelectAll,
    };
  },
};
</script>
